import axios from 'axios'
import React, { useState, useMemo, useEffect, useContext } from "react";
import xls from "../../images/download-file-icon.png";
import Constants from '../../Constants';
import { UserContext } from "../../Contexts/UserContext";
import { toast } from 'react-toastify';
import { Tooltip, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { RiMailDownloadLine } from "react-icons/ri";
import { authGenerateUrl, authVerifyUrl } from '../../Config/config';
import DownloadTrigger from '../ExportPopup/DownloadTrigger';


/** The `ExcelDownloader` component that allows users to download an Excel file. */
function ExcelDownloader({ url, data: exportData, children, fileName = "file", type = "xlsx" }) {
    const [isDownloading, setIsDownloading] = useState(false);

    const { getToken, logout, getUser, authAlert } = useContext(UserContext);
    const [authCode, setAuthCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [reachedLimitPopup, setReachedLimitPopup] = useState({ show: false, message: "", exportLevelCaution: false })


    const onDownloadClick = async () => {
        if (authAlert()) {
            setLoading(true)

            try {
                const generateResponse = await axios.post(authGenerateUrl, {}, {
                    headers: {
                        "Authorization": getToken(),
                    },
                });

                if (generateResponse.status === 200) {
                    setReachedLimitPopup({
                        show: true,
                        message: "An authorization code has been sent to your registered email ID. Please enter it below to proceed with data export through the database.",
                    });
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error generating authorization code:", error);
                setLoading(false);
                toast.error("Failed to generate authorization code. Please try again.", { position: "top-center" });
            }
            return;
        }

        const result = await downloadExcel()
        if (!result.status) {
            setReachedLimitPopup({ show: true, message: result.message, exportLevelCaution: result.message?.includes("Do you want to proceed?") })
        }
    }

    const handleCautionPopupYes = async () => {
        const result = await downloadExcel(true)
        if (result.status) {
            setReachedLimitPopup({ show: false, message: "", exportLevelCaution: false })
        } else {
            setReachedLimitPopup({ show: true, message: result.message, exportLevelCaution: result.message?.includes("Do you want to proceed?") })
        }
    }

    const downloadExcel = async (bypassCountCheck = false) => {
        try {
            setIsDownloading(true);

            const response = await axios.post(url, { ...exportData, count_records: !bypassCountCheck }, {
                headers: {
                    'Authorization': getToken(),
                    'Content-Type': 'application/json',
                },
                responseType: 'blob', // Set responseType to 'blob' to handle file downloads
            });

            const contentType = response.headers['content-type'];

            if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                // Handle file download
                const blob = new Blob([response.data], { type: contentType });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', `${fileName}.${type}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                return { status: true };
            } else {
                // Parse JSON response for non-file content
                const text = await response.data.text();
                const data = JSON.parse(text);
                toast.success(data?.message, { position: "top-center" });
                return { status: true };
            }
        } catch (error) {
            console.error("downloadExcel - error", error);
            if (error?.response?.status === Constants.sessionTerminatedStatus) {
                logout();
            } else if (error?.response?.status === 400) {
                const dataAsText = await error?.response?.data?.text();
                const parsedData = JSON.parse(dataAsText);
                return {
                    status: false,
                    message: parsedData?.message || "Something went wrong during export",
                };
            }
            return {
                status: false,
                message: error?.response?.data?.message || "Something went wrong during export",
            };
        } finally {
            setIsDownloading(false);
        }

    }


    const handleAuthCodeSubmit = async (code) => {
        try {
            // Step 3: Call authVerifyUrl API with the entered code
            const verifyResponse = await axios.post(authVerifyUrl, { authCode: code }, {
                headers: {
                    "Authorization": getToken(),
                },
            });

            if (verifyResponse.status === 200) {
                console.log(verifyResponse, "Authorization code verified successfully");
                // Step 4: Show success message and proceed with export
                toast.success(verifyResponse?.data?.message, { position: "top-center" });
                localStorage.setItem(Constants.localStorageKeys.exportExpireAt, verifyResponse?.data?.export_expires_at);

                setReachedLimitPopup({ show: false, message: "", exportLevelCaution: false });

                const result = await downloadExcel()
                if (!result.status) {
                    setReachedLimitPopup({ show: true, message: result.message, exportLevelCaution: result.message?.includes("Do you want to proceed?") })
                }
            }
        } catch (error) {
            console.error("Error verifying authorization code:", error.response?.data?.message);
            toast.error(error?.response?.data?.message, { position: "top-center" });
        }
    };
    if (reachedLimitPopup.show ) {
        if(authAlert()){
            return (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75 z-10">
                    <div className="relative w-full max-w-lg mx-4 md:mx-0 bg-white rounded-lg shadow-xl">
                        {/* Header */}
                        <div className="pt-5 px-5 flex justify-between items-center">
                            <h1 className="text-lg font-bold text-gray-900">Authorization Required</h1>
                            <button
                                onClick={() => setReachedLimitPopup({ show: false, message: "", exportLevelCaution: false })}
                                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-5">
                            <p className="text-sm text-gray-500">{reachedLimitPopup.message}</p>
                            <div className="mt-4 pl-4 flex items-center gap-2">
                                <input
                                    type="text"
                                    maxLength={8}
                                    // placeholder="52974198"
                                    className="w-28 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20]"
                                    onChange={(e) => setAuthCode(e.target.value)}
                                />
                                <button
                                    onClick={() => handleAuthCodeSubmit(authCode)}
                                    className="px-4 py-2 text-sm font-medium text-white bg-[#BC8B20] rounded-md shadow-sm hover:bg-[#a37517] focus:outline-none"
                                    disabled={authCode.length !== 8}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }else{
            return <div className='flex font-sans_book_body cursor-default justify-center items-center z-10 fixed left-0 top-0 w-screen h-screen  bg-gray-500 bg-opacity-75 transition-opacity'>
            <div className='relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 mx-4 md:mx-0 w-[50%]'>
                <div className='w-full flex flex-col '>
                    <div className="mt-3 text-center sm:mt-0 px-5 py-3 sm:ml-4 sm:text-left">
                        <h1 className='text-left py-1 text-lg font-bold leading-6 text-gray-900'>Alert!</h1>
                        <div className="mt-4 ">
                            <p className="text-sm text-gray-500 text-start">{reachedLimitPopup.message}</p>
                        </div>
                    </div>
                </div>
                <div className='flex bg-gray-50 px-4 pb-3 flex-row-reverse'>
                    <button onClick={() => setReachedLimitPopup({ show: false, message: "", exportLevelCaution: false })} className='inline-flex w-full justify-center font-sans_bold_body rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none  sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'>
                        {reachedLimitPopup.exportLevelCaution ? "No" : "Close"}
                    </button>
                    {reachedLimitPopup.exportLevelCaution && (
                        <button onClick={handleCautionPopupYes} disabled={isDownloading} className='inline-flex w-full justify-center font-sans_bold_body rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:cursor-progress sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'>
                            {isDownloading ? "Downloading" : "Yes"}
                        </button>
                    )}
                </div>
            </div>
        </div>
        }
    }

    const isBusy = isDownloading || loading;

    return (
        <div>
            <span onClick={isBusy ? undefined : onDownloadClick}>
                {isBusy
                    ? <Spin indicator={<LoadingOutlined style={{ fontSize: 20, color: "#BC8B20" }} spin />} />
                    : children || <DownloadTrigger />
                }
            </span>
        </div>
    )
}

export default ExcelDownloader